// src/services/api/statsApi.js
//
// Wrapper for GET /api/v1/pipeline/runs/stats — aggregated run counters
// for the Dashboard. Mirrors src/admin/runsApi.js conventions where they
// apply, with two deliberate differences:
//   - suppressTenantHeader is CONDITIONAL on `tenant` being supplied.
//     runsApi callers always pass ?tenant=, so suppressing the header is
//     safe there. Dashboard does NOT pass ?tenant= by default and needs
//     the X-Tenant-Id header from localStorage for tenant scope; we only
//     suppress when the caller supplies an explicit override.
//   - Dashboard callers also pass `tenant: activeTenant.tenant_id` to keep
//     the URL tenant-keyed (browser cache leak protection — BE serves
//     Cache-Control: private, max-age=60 and the URL is the only cache key).
//   - Date helpers match /runs semantics: `before` is treated as an EXCLUSIVE
//     upper bound on created_at, so when the caller passes a YYYY-MM-DD we
//     send start-of-NEXT-day to keep that date inclusive in the range.
//   - UNKNOWN_QUERY_PARAM (422) is surfaced with an Italian message.
//
import i18n from '../../i18n/index.js';
import { apiFetch } from '../../auth/apiFetch.js';

const PIPELINE = '/api/v1/pipeline';

// YYYY-MM-DD → ISO naive UTC start-of-day. Pass-through full ISO datetimes.
function toSinceIso(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`;
  return value;
}

// YYYY-MM-DD → start-of-NEXT-day (exclusive upper bound). Pass-through full ISO.
function toBeforeIsoExclusive(value) {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [y, m, d] = value.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const next = new Date(Date.UTC(y, m - 1, d));
  next.setUTCDate(next.getUTCDate() + 1);
  const yyyy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T00:00:00`;
}

/**
 * Fetch aggregated run counts.
 *
 * @param {Object} opts
 * @param {'status'|'platform'|'content_format'|'day'} opts.groupBy  REQUIRED.
 *   The BE allowlist is exactly this set. 'error_code' was speculative and
 *   is NOT supported — the BE returns 422 UNKNOWN_QUERY_PARAM.
 * @param {string} [opts.since]        YYYY-MM-DD or full ISO
 * @param {string} [opts.before]       YYYY-MM-DD or full ISO (exclusive)
 * @param {string} [opts.tenant]       Tenant id
 * @param {boolean} [opts.includeMock] default false
 *
 * @deprecated opts.breakdown — BE rejects with 422. Do NOT pass.
 * @deprecated opts.status    — BE rejects with 422. Do NOT pass.
 *   Both kept in the signature only to avoid a test-suite sweep; new callers
 *   must never set them. Will be removed in a follow-up once no tests
 *   reference them.
 */
export async function fetchRunsStats({
  groupBy,
  since,
  before,
  tenant,
  includeMock = false,
  // Deprecated — accepted but ignored. Were dropped on the BE side.
  breakdown: _deprecatedBreakdown,
  status: _deprecatedStatus,
} = {}) {
  if (!groupBy) throw new Error("fetchRunsStats: 'groupBy' is required");

  const params = new URLSearchParams();
  params.set('group_by', groupBy);
  if (tenant) params.set('tenant', tenant);
  const sinceIso = toSinceIso(since);
  const beforeIso = toBeforeIsoExclusive(before);
  if (sinceIso) params.set('since', sinceIso);
  if (beforeIso) params.set('before', beforeIso);
  if (includeMock) params.set('include_mock', 'true');

  const res = await apiFetch(`${PIPELINE}/runs/stats?${params}`, {
    suppressTenantHeader: !!tenant,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (body.error_code === 'UNKNOWN_QUERY_PARAM') {
      throw new Error(i18n.t('errors:api.unrecognizedParam', { detail: body.message || res.status }));
    }
    if (body.error_code === 'INVALID_GROUP_BY') {
      throw new Error(i18n.t('errors:stats.invalidGroupBy', { detail: body.message || groupBy }));
    }
    throw new Error(body.message || `Failed to fetch run stats: ${res.status}`);
  }
  return res.json();
}
